import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { interval, Observable, of, Subscription, throwError, timer } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { environment } from 'environments/environment';
import { jwtDecode } from 'jwt-decode';
import { NzModalService } from 'ng-zorro-antd/modal';
import { UserService } from './user.service';
import { Router } from '@angular/router';
import { roleService } from 'src/app/services/role.service';
import { TranslateService } from '@ngx-translate/core';

/** -oidc keepalive- refresh_token de la session NAVIGATEUR Keycloak (saut "Accéder"). */
export const BROWSER_REFRESH_KEY = 'kc_browser_refresh';
// -slo- toutes les 1 min (filet de securite) : la verification principale se fait au chargement
// de la page et au retour sur l'onglet. ponytail: instantane meme onglet visible = back-channel logout
const BROWSER_SESSION_REFRESH_MS = 60 * 1000;

@Injectable({
  providedIn: 'root'
})
export class AuthService {
private refreshTokenTimeout?: Subscription;

  private tokenUrl = 'https://intranet.utm.rnu.tn/auth/realms/master/protocol/openid-connect/token';

  constructor(
    private http: HttpClient,
    private modal: NzModalService,
    private userservice: UserService,
    private router: Router,
    private roleService: roleService,
    private t: TranslateService
  ) {
    // -oidc keepalive- Garde vivante la session NAVIGATEUR Keycloak (celle des apps OIDC)
    // tant que socle est ouvert et connecte : sinon Keycloak la ferme apres "SSO Session
    // Idle" (30 min) d'inactivite, alors que socle, lui, reste connecte.
    interval(BROWSER_SESSION_REFRESH_MS).subscribe(() => this.refreshBrowserSession());
    // -slo- verification immediate au chargement (F5 / ouverture) et au retour sur l'onglet socle
    this.refreshBrowserSession();
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') { this.refreshBrowserSession(); }
    });
  }

  /**
   * -oidc keepalive- Rafraichit la session navigateur Keycloak creee par le saut
   * silencieux (bouton "Accéder" d'une app OIDC/SAML). Rafraichir le jeton compte comme
   * une activite : le compteur d'inactivite Keycloak repart a zero.
   * -slo- Keycloak repond invalid_grant = sa session n'existe plus (Se déconnecter
   * dans une app, expiration) -> Single Logout : socle se deconnecte aussi.
   * Autre echec (reseau, Keycloak indisponible) : on oublie la cle, SANS deconnecter socle.
   */
  refreshBrowserSession(): void {
    const refreshToken = localStorage.getItem(BROWSER_REFRESH_KEY);
    if (!refreshToken || !this.isAuthenticated()) {
      return;
    }
    // -slo- pages /authentication (login-1 recoit le code du saut "Accéder" et va
    // remplacer la cle) : pas de verification ici, pour ne jamais tester une cle perimee.
    // -slo login- seulement si l'adresse porte ?code= : sans code (ex. retour de "خروج" d'une app OIDC
    // vers login-1), la session fermee est vue tout de suite au lieu d'afficher socle connecte jusqu'au F5.
    if (window.location.pathname.startsWith('/authentication') && new URLSearchParams(window.location.search).has('code')) {
      return;
    }
    const body = new HttpParams()
      .set('client_id', 'socle-backend')
      .set('grant_type', 'refresh_token')
      .set('client_secret', '9Qo7O8germAOrcVifRQbeboIDY8yNfTB')
      .set('refresh_token', refreshToken);
    this.http.post<any>(this.tokenUrl, body, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    }).subscribe({
      next: (res) => {
        if (res?.refresh_token) { localStorage.setItem(BROWSER_REFRESH_KEY, res.refresh_token); }
        if (res?.id_token) { localStorage.setItem('id_token', res.id_token); } // logout = session a jour
      },
      error: (err) => {
        // Un autre onglet socle a deja remplace la cle entre-temps : cet echec ne prouve rien.
        if (localStorage.getItem(BROWSER_REFRESH_KEY) !== refreshToken) { return; }
        localStorage.removeItem(BROWSER_REFRESH_KEY);
        if (err?.status === 400 && err?.error?.error === 'invalid_grant') {
          // Session Keycloak deja fermee : id_token retire -> logout() local, sans
          // aller-retour inutile chez Keycloak avec un jeton d'une session morte.
          localStorage.removeItem('id_token');
          this.logout();
        }
      },
    });
  }

//login(username: string, password: string): Observable<any> {
 login(username: string, password: string, recaptchaToken: string): Observable<any> {
   const body = { username, password,recaptchaToken };
    //const body = { username, password };
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return this.http.post<any>(`${environment.apiUrl}/auth/connexion`, body, { headers })
      .pipe(
        catchError(error => {
          // -verify- Laisser passer les etats de compte (non verifie / desactive)
          // pour que le composant affiche le pop-up "Verifier" ou le message
          // "desactive". Toutes les autres erreurs gardent la modale generique.
          const reason = error?.error?.error;
          if (reason === 'account_unverified' || reason === 'account_disabled') {
            // RxJS 6 (projet en ~6.6.0) : throwError(error) emet bien l'erreur
            // elle-meme. NE PAS utiliser throwError(() => error) — ca, c'est la
            // syntaxe RxJS 7 : en RxJS 6 elle emettrait la FONCTION comme erreur,
            // donc error.error.error serait undefined cote composant et le pop-up
            // de verification ne s'afficherait jamais (message generique a la place).
            return throwError(error);
          }
          this.modal.error({
           nzTitle: this.t.instant('auth.loginErrorTitle'),
          nzContent: this.t.instant('auth.badCredentials'),
          nzOkText: 'OK',
           nzOkType:'default'
           });
          return of(null);
        })

      );
               
  }
  

/**
   * -saml changes- (les deux méthodes sessionInit + buildSilentSessionUrl)
   *
   * Session Keycloak silencieuse (aller-retour 302, aucune page visible).
   * Après le login CIN/mdp (password grant : aucun cookie Keycloak créé),
   * on demande au backend un code à usage unique (/idp/session-init) puis on
   * redirige le navigateur vers Keycloak avec kc_idp_hint=socle : Keycloak
   * délègue à l'IdP "socle", récupère l'identité, pose son cookie de session
   * et revient sur login-1?code=... (traité par le flux existant, comme
   * Google). Résultat : l'app SAML et les apps OIDC s'ouvrent ensuite sans
   * page de login Keycloak.
   */
  sessionInit(): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.post<any>(`${environment.apiUrl}/idp/session-init`, {}, {
      headers: new HttpHeaders({ Authorization: `Bearer ${token}` })
    });
  }

  /**
   * -saml changes- Échange le code du saut silencieux contre des jetons liés à
   * la session NAVIGATEUR Keycloak (celle qui porte le cookie). Volontairement
   * sans handleGoogleLogin/save-user : ici l'utilisateur est déjà enregistré,
   * on ne veut que l'id_token pour la déconnexion.
   */
  async exchangeSilentCode(code: string): Promise<any> {
    const body = new HttpParams()
      .set('client_id', 'socle-backend')
      .set('grant_type', 'authorization_code')
      .set('redirect_uri', 'https://intranet.utm.rnu.tn/authentication/login-1')
      .set('client_secret', '9Qo7O8germAOrcVifRQbeboIDY8yNfTB')
      .set('code', code);

    const res: any = await this.http.post(this.tokenUrl, body, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    }).toPromise();

    // On ne récupère QUE l'id_token : les jetons de travail (password grant)
    // restent inchangés, aucun risque pour la session en cours.
    if (res && res.id_token) {
      localStorage.setItem('id_token', res.id_token);
    }
    // -oidc keepalive- refresh_token de CETTE session navigateur : rafraichi
    // periodiquement (refreshBrowserSession) pour qu'elle ne meure pas d'inactivite.
    if (res && res.refresh_token) {
      localStorage.setItem(BROWSER_REFRESH_KEY, res.refresh_token);
    }
    return res;
  }

  /**
   * -google direct- Envoie le code Google au backend, qui l'échange et vérifie
   * l'identité + le statut Keycloak côté serveur, puis renvoie un hint. Le front
   * enchaîne buildSilentSessionUrl(hint) : même saut silencieux que le login CIN.
   * Un compte désactivé/non vérifié renvoie une erreur ici (page socle), jamais
   * une page Keycloak.
   */
  googleLogin(code: string): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}/auth/google-login`, { code });
  }

  buildSilentSessionUrl(hint: string): string {
    return 'https://intranet.utm.rnu.tn/auth/realms/master/protocol/openid-connect/auth'
      + '?client_id=socle-backend'
      + '&response_type=code'
      + '&redirect_uri=' + encodeURIComponent('https://intranet.utm.rnu.tn/authentication/login-1')
      + '&scope=' + encodeURIComponent('openid email profile')
      + '&kc_idp_hint=socle'
      + '&login_hint=' + encodeURIComponent(hint);
  }

register(user: any): Observable<any> {
  const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

  return this.http.post<any>(`${environment.apiUrl}/auth/register`, user, { headers }) 
    .pipe(
         tap(response => {
        if (response && response.requiresOtp) {
          this.modal.info({
            nzTitle: this.t.instant('auth.verifyRequired'),
            nzContent: this.t.instant('auth.codeSent'),
            nzOkText: 'OK',
            nzOkType:'default'
          });
        }
      }),
        catchError(error => {
          this.modal.error({
          nzTitle: this.t.instant('auth.registerErrorTitle'),
          nzContent: this.buildRegisterErrorMessage(error),
          nzOkText: 'OK',
           nzOkType:'default'
           });
          return of(null);
        })
    );
}

/**
 * Construit un message d'erreur clair pour l'inscription à partir de la réponse
 * du backend, sans modifier la logique d'inscription (affichage uniquement).
 */
private buildRegisterErrorMessage(error: any): string {
  // Pas de réponse réseau (serveur injoignable / hors ligne)
  if (error?.status === 0) {
    return this.t.instant('auth.serverUnreachable');
  }

  const backendMsg: string = error?.error?.message || '';
  const haystack = `${backendMsg} ${JSON.stringify(error?.error ?? '')}`.toLowerCase();

  // Garde-fou backend (409) : message clair selon la source du compte (Google / mot de passe).
  if (error?.status === 409) {
    return backendMsg || this.t.instant('auth.accountExists');
  }

  // Conflit Keycloak renvoyé en 500 (ex. N° de Cin déjà pris) : message générique propre.
  if (haystack.includes('exist') || haystack.includes('duplicate')) {
    return this.t.instant('auth.accountExists');
  }

  // Erreurs de validation renvoyées par le backend (400)
  if (error?.status === 400) {
    const validationMsg = this.extractValidationMessage(error);
    return validationMsg || this.t.instant('auth.invalidData');
  }

  // Message explicite fourni par le backend
  if (backendMsg) {
    return backendMsg;
  }

  return this.t.instant('auth.registerError');
}

/**
 * Extrait les messages de validation (@Valid) renvoyés par Spring, quel que soit
 * le format (liste d'erreurs, map champ -> message, ou champ « errors »).
 */
private extractValidationMessage(error: any): string {
  const body = error?.error;
  if (!body) {
    return '';
  }
  // Format { errors: [{ defaultMessage }] } ou { errors: ["..."] }
  if (Array.isArray(body.errors)) {
    const msgs = body.errors
      .map((e: any) => (typeof e === 'string' ? e : e?.defaultMessage || e?.message))
      .filter((m: any) => !!m);
    if (msgs.length) {
      return msgs.join(' ');
    }
  }
  // Format map { champ: "message" }
  if (typeof body === 'object') {
    const values = Object.values(body).filter(
      (v: any) => typeof v === 'string' && v.length > 0 && v !== body.message
    );
    if (values.length) {
      return values.join(' ');
    }
  }
  return '';
}

verifyOtp(data: { email: string, code: string }): Observable<any> {
  const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
  return this.http.post<any>(`${environment.apiUrl}/auth/verify-otp`, data, { headers })
    .pipe(
      tap(response => {
        if (response && response.success === true) {
          this.modal.success({
            nzTitle: this.t.instant('auth.verifySuccessTitle'),
            nzContent: this.t.instant('auth.verifySuccess'),
            nzOkText: 'OK',
            nzOkType: 'default'
          });
        }
      }),
      catchError(error => {
        this.modal.error({
          nzTitle: this.t.instant('auth.verifyErrorTitle'),
          nzContent: error?.error?.message || this.t.instant('auth.codeInvalid'),
          nzOkText: 'OK',
          nzOkType: 'default'
        });
        return of(null);
      })
    );
}

resendOtp(data: { email: string }): Observable<any> {
  const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
  return this.http.post<any>(`${environment.apiUrl}/auth/resend-otp`, data, { headers })
    .pipe(
      tap(response => {
        if (response && response.success === true) {
          this.modal.success({
            nzTitle: this.t.instant('auth.codeResentTitle'),
            nzContent: this.t.instant('auth.codeResent'),
            nzOkText: 'OK',
            nzOkType: 'default'
          });
        }
      }),
      catchError(error => {
        this.modal.error({
          nzTitle: this.t.instant('common.error'),
          nzContent: error?.error?.message || this.t.instant('auth.resendFailed'),
          nzOkText: 'OK',
          nzOkType: 'default'
        });
        return of(null);
      })
    );
}


isAuthenticated(): boolean {
  return !!localStorage.getItem('token');  // Vérifie si le token est présent dans le stockage local
}


async exchangeCodeForToken(code: string): Promise<any> {
  const body = new HttpParams()
    .set('client_id', 'socle-backend')
    .set('grant_type', 'authorization_code')
    .set('redirect_uri', 'https://intranet.utm.rnu.tn/authentication/login-1')
    .set('client_secret', '9Qo7O8germAOrcVifRQbeboIDY8yNfTB')
    .set('code', code);

  try {
    const tokenResponse = await this.http.post(this.tokenUrl, body, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    }).toPromise();

    // Ici tu as le token, tu peux appeler handleGoogleLogin
    if (tokenResponse && tokenResponse['access_token']) {
      await this.handleGoogleLogin(tokenResponse['access_token']);
    }

    // Retourne la réponse si besoin
    return tokenResponse;

  } catch (error) {
    throw error; 
  }
}


async handleGoogleLogin(token: string) {
  try {
    
    const accessToken = token;
    // Appel à Keycloak /userinfo
    const userInfo = await this.http.get('https://intranet.utm.rnu.tn/auth/realms/master/protocol/openid-connect/userinfo', {
      headers: { Authorization: `Bearer ${accessToken}` },
    }).toPromise();


    // Envoi vers le backend  pour enregistrement en base
    // On joint le token Bearer (comme pour /userinfo ci-dessus) : sinon le backend
    // renvoie 401 et la connexion Google échoue ("Impossible de finaliser la connexion").
    const localUser = await this.http.post(`${environment.apiUrl}/auth/save-user`, userInfo, {
      headers: { Authorization: `Bearer ${accessToken}` },
    }).toPromise();

    // Stocke user/local token si nécessaire
  } catch (error) {
      throw error; 
  }
}



 handleLoginResponse(response: any) {
    if (response && response.access_token && response.refresh_token && response.expires_in) {
      localStorage.setItem('token', response.access_token);
      localStorage.setItem('refresh_token', response.refresh_token);
      // -saml changes- id_token conservé pour la déconnexion Keycloak
      if (response.id_token) {
        localStorage.setItem('id_token', response.id_token);
      }


      // expires_in est en secondes, converti en timestamp ms
      const expiresAt = Date.now() + response.expires_in * 1000;
      localStorage.setItem('expires_at', expiresAt.toString());
      

      this.startRefreshTokenTimer(expiresAt.toString());
    } else {
      this.logout();
    }
  }

public startRefreshTokenTimer(expiresAtStr: string | null) {
  if (!expiresAtStr) {
    return;
  }

  const expiresAt = parseInt(expiresAtStr, 10);
  const now = Date.now();
  const refreshTime = expiresAt - now - 60 * 1000; // 1 minute avant expiration

  if (refreshTime <= 0) {
    this.doRefreshToken().subscribe({
      next: (response) => {
        this.handleLoginResponse(response);
      },
      error: () => {
        this.logout();
      }
    });
    return;
  }

  if (this.refreshTokenTimeout) {
    this.refreshTokenTimeout.unsubscribe();
  }


  this.refreshTokenTimeout = timer(refreshTime).subscribe(() => {
    this.doRefreshToken().subscribe({
      next: (response) => {
        this.handleLoginResponse(response);
      },
      error: () => {
        this.logout();
      }
    });
  });
}


  doRefreshToken(): Observable<any> {
    const refresh_token = localStorage.getItem('refresh_token');
    if (!refresh_token) {
      this.logout();
      return throwError(() => new Error('No refresh token available'));
    }

    return this.http.post<any>(`${environment.apiUrl}/auth/refresh-token`, { refresh_token }).pipe(
      catchError(err => {
        this.logout();
        return throwError(() => err);
      })
    );
  }

  checkTokenExpiration() {
    const expiresAt = localStorage.getItem('expires_at');
    if (expiresAt && Date.now() > +expiresAt) {
      this.logout();
    }
  }

  logout() {
    // -saml changes- Déconnexion SSO par le NAVIGATEUR (front-channel).
    // Le login pose un cookie de session Keycloak ; seul Keycloak peut
    // supprimer son propre cookie, et seulement si le navigateur passe chez
    // lui. Une API admin côté serveur ne supprime pas ce cookie — d'où
    // l'ancien symptôme : après logout, le bouton Google reconnectait
    // silencieusement le compte précédent.
    // id_token_hint évite la page "Voulez-vous vous déconnecter ?" (KC 19)
    // et post_logout_redirect_uri ramène directement sur le login du socle.
    const idToken = localStorage.getItem('id_token');

    localStorage.clear();
    sessionStorage.clear();
    this.roleService.clearRoles();
    this.roleService.clearToken();
    if (this.refreshTokenTimeout) {
      this.refreshTokenTimeout.unsubscribe();
    }

    if (idToken) {
      const back = encodeURIComponent('https://intranet.utm.rnu.tn/authentication/login-1');
      window.location.href =
        'https://intranet.utm.rnu.tn/auth/realms/master/protocol/openid-connect/logout'
        + '?id_token_hint=' + idToken
        + '&post_logout_redirect_uri=' + back;
    } else {
      // Pas d'id_token (ancienne session) : logout local comme avant.
      this.router.navigate(['/authentication/login-1']);
    }
  }
}